import React, { memo, useCallback, useMemo } from 'react';
import useTicketsFetcher from './hooks/useTicketsFetcher';
import { FormControl, FormHelperText, InputLabel, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ellipsis } from 'src/utils/StringUtils';
import Ticket, { TicketSearchCriteria } from 'src/interfaces/api/Ticket';
import useSitesFetcher from './hooks/useSitesFetcher';

interface Props {
  className?: string;
  domainId?: string;
  label: string;
  value: string;
  helperText?: string;
  error?: boolean;
  onChange: () => void;
}

const TICKETS_LIMIT = 50;

const TicketSelect = React.forwardRef<any, Props>((props, ref) => {
  const { className, domainId, label, onChange, value, helperText, ...otherProps } = props;
  const ticketFilter: TicketSearchCriteria = useMemo(() => ({ stateId: 5, global: 0, closed: false }), []);

  const { loading, error, total, tickets } = useTicketsFetcher(ticketFilter, TICKETS_LIMIT, 0, 'createdAt', 'desc');
  const { sites, loading: sitesLoading, error: sitesError } = useSitesFetcher(tickets);

  const getTicketsfilteredByDomain = useCallback((): Ticket[] => {
    if (domainId) {
      return tickets.filter((ticket) => ticket.domainId === domainId);
    }
    return tickets;
  }, [domainId, tickets]);

  const hasValueInTicketsList = useCallback((): boolean => {
    if (!value) {
      return true;
    }

    return tickets.filter((ticket) => ticket.id === value).length > 0;
  }, [value, tickets]);

  const getSite = useCallback((id?: string) => sites.find((site) => site.id === id)?.socialReason || '', [sites]);

  if (loading || sitesLoading) {
    return <Skeleton height={50} />;
  }

  if (error || sitesError) {
    return <div style={{ color: '#f44336' }}>{error || sitesError}</div>;
  }

  return (
    <FormControl ref={ref} className={className} variant='outlined' fullWidth {...otherProps}>
      <InputLabel>{label}</InputLabel>
      <Select native value={value} onChange={onChange} label={label}>
        <option value='' />
        {getTicketsfilteredByDomain().map((ticket, key) => (
          <option key={key} value={ticket.id}>
            {`${ticket.id} ${getSite(ticket.siteId)} - ${ellipsis(ticket.subject, 40)}`}
          </option>
        ))}

        {!hasValueInTicketsList() && <option value={value}>{value}</option>}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      {total > TICKETS_LIMIT && (
        <FormHelperText>Seuls les {TICKETS_LIMIT} premiers tickets sont affichés</FormHelperText>
      )}
    </FormControl>
  );
});

export default memo(TicketSelect);
